import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, Card, Field, Notice } from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';

export function RegisterPage() {
  const navigate = useNavigate();
  const { reload } = useSession();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await api.request<{
        accessToken: string;
        accessTokenExpiresAt: string;
        sessionToken: string;
        sessionExpiresAt: string;
        mfaSatisfied: boolean;
      }>('/api/v1/auth/register', {
        method: 'POST',
        anonymous: true,
        body: { email, password, displayName },
      });
      api.setSession(session);
      await reload();
      void navigate('/organizations', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="gc-auth">
      <div className="gc-auth__card gc-stack">
        <span className="gc-wordmark">
          GateCheck<span className="gc-wordmark__accent">Pro</span>
        </span>

        <Card>
          <form className="gc-stack" onSubmit={(event) => void submit(event)}>
            <h1>Create your account</h1>
            <p className="gc-muted">
              This is your personal sign-in. You will create or join an organization next.
            </p>

            <FailureNotice error={error} />

            <Field
              label="Your name"
              name="displayName"
              autoComplete="name"
              required
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Field
              label="Password"
              type="password"
              name="password"
              autoComplete="new-password"
              required
              minLength={12}
              hint="At least 12 characters. Length beats punctuation."
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            {/*
              Said plainly rather than implied. There is no messaging adapter
              until Slice 4, and a product that shows "check your inbox" when
              nothing was sent has lied to its first user.
            */}
            <Notice tone="info" title="Email delivery is not live yet">
              Verification messages are not sent in this release. Your account works; the verified
              badge arrives when messaging ships.
            </Notice>

            <Button type="submit" variant="primary" size="large" block busy={busy}>
              Create account
            </Button>
          </form>
        </Card>

        <p className="gc-muted">
          Already have an account? <Link to="/sign-in">Sign in</Link>
        </p>
      </div>
    </main>
  );
}
